import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useMediaQuery } from '@mui/material';

const CustomTooltip = ({
	varrient,
	text,
	position,
	children,
	width,
	customPosition,
	disabled,
	customStyle,
	expiry,
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [open, setOpen] = useState(false);
	const isMobile = useMediaQuery('(max-width: 767px)');

	const hasTooltipContent = (value) =>
		value !== undefined &&
    value !== null &&
    !(typeof value === 'string' && value.trim() === '');

	const shouldShowTooltip = () => hasTooltipContent(text);

	useEffect(() => {
		if (!open) {
			return undefined;
		}

		const handleWindowBlur = () => {
			setAnchorEl(null);
			setOpen(false);
		};

		window.addEventListener('blur', handleWindowBlur);
		return () => window.removeEventListener('blur', handleWindowBlur);
	}, [open]);

	const handleOpenTooltip = (event) => {
		if (disabled || !shouldShowTooltip()) {
			return;
		}
		setAnchorEl(event.currentTarget);
		setOpen(true);
	};

	const handleCloseTooltip = () => {
		if (disabled) {
			return;
		}
		setAnchorEl(null);
		setOpen(false);
	};

	const style = {
		backgroundColor: shouldShowTooltip() ? '#808191' : '',
		padding: '4px 8px',
		borderRadius: '8px',
		maxWidth: isMobile ? '250px' : width || '310px',
		position: 'relative',
		color: 'white',
		...(customPosition ? customPosition : {}),
	};

	if (varrient === 'result-sidebar-tooltip') {
		return (
			<span
				style={{ width: width || 'auto' }}
				onMouseEnter={handleOpenTooltip}
				onMouseLeave={handleCloseTooltip}
			>
				{children}
				<Popper
					open={open}
					anchorEl={anchorEl}
					// placement={position || 'bottom-start'}
				>
					{({ TransitionProps }) => (
						<div
							className="d-flex align-items-center"
							{...TransitionProps}
							style={{
								transformOrigin: getTransformOrigin(position),
								...style,
							}}
						>
							<span className="body-4" style={{ color: 'white' }}>
								{text}
							</span>
						</div>
					)}
				</Popper>
			</span>
		);
	} else if (varrient === 'tooltip-with-expiry') {
		return (
			<span
				style={{ ...customStyle, width: width || 'auto' }}
				onMouseEnter={handleOpenTooltip}
				onMouseLeave={handleCloseTooltip}
			>
				{children}
				<Popper
					open={open}
					anchorEl={anchorEl}
					placement={position || 'bottom-start'}
				>
					{({ TransitionProps }) => (
						<div
							className="d-flex align-items-center"
							{...TransitionProps}
							style={{
								transformOrigin: getTransformOrigin(position),
								...style,
							}}
						>
							<span className="body-4" style={{ color: 'white' }}>
								{text}{' '}
								{expiry && <span style={{ marginLeft: '64px' }}>{expiry}</span>}
							</span>
						</div>
					)}
				</Popper>
			</span>
		);
	} else {
		return (
			<span
				style={{ ...customStyle, width: width || 'auto' }}
				onMouseEnter={handleOpenTooltip}
				onMouseLeave={handleCloseTooltip}
			>
				{children}
				<Popper
					open={open}
					anchorEl={anchorEl}
					placement={position || 'bottom-start'}
				>
					{({ TransitionProps }) => (
						<div
							className="d-flex align-items-center"
							{...TransitionProps}
							style={{
								transformOrigin: getTransformOrigin(position),
								...style,
							}}
						>
							<span className="body-4" style={{ color: 'white' }}>
								{text}
							</span>
						</div>
					)}
				</Popper>
			</span>
		);
	}
};

// Utility function to determine transform origin based on placement
const getTransformOrigin = (position) => {
	if (!position) {
		position = 'bottom-start';
	}
	const [vertical, horizontal] = position.split('-');
	const axis = vertical === 'bottom' ? 'top' : 'bottom';
	return `${axis} ${horizontal}`;
};

CustomTooltip.propTypes = {
	text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	position: PropTypes.oneOf([
		'top-start',
		'top',
		'top-end',
		'left-start',
		'left',
		'left-end',
		'right-start',
		'right',
		'right-end',
		'bottom-start',
		'bottom',
		'bottom-end',
	]).isRequired,
	children: PropTypes.node.isRequired,
};

export default CustomTooltip;
